<template>
  <fragment>
    <td class="px-1">
      <px-autocomplete-produto
        dense
        show-details
        menu-props="auto"
        :truncate-value="50"
        :clearable="false"
        v-model="produto.produto"
        class="required rounded-tag-small"
        base-path="/produtos/page"
        :error="$v.produto.produto.$error"
        @blur="$v.produto.produto.$touch()"
        @change="onChange(produto)">
        <template v-slot:append v-if="produto">
          <div v-if="produto.produto">
            <px-resumo-produto-dialog :produto="produto.produto"
                                      :unidade-medida="produto.unidadeMedida"/>
          </div>
        </template>
      </px-autocomplete-produto>
    </td>
    <td class="px-1">
      <px-input-decimal-number
        dense
        required
        ref="quantidade"
        label="Quantidade"
        class="required rounded-tag-small"
        :precision="getPrecision(produto.produto)"
        :error="$v.produto.quantidade.$error"
        @blur="$v.produto.quantidade.$touch()"
        v-model="produto.quantidade"/>
    </td>
    <td class="px-1">
      <px-autocomplete-unidade-medida
        dense
        required
        :clearable="false"
        :only-open="false"
        class="required rounded-tag-small"
        :error="$v.produto.unidadeMedida.id.$error"
        @blur="$v.produto.unidadeMedida.id.$touch()"
        v-model="produto.unidadeMedida"/>
    </td>
    <td class="px-1">
      <v-textarea
        outlined
        auto-grow
        dense
        hide-details
        v-model="produto.especificacaoProduto"
        label="Especificação"
        counter="1500"
        rows="1"
        class="rounded-tag-small"/>
    </td>
    <td class="px-1">
      <px-cadastro-produto-observacoes @file-loading="onFileLoad"
                                       :produto="produto"/>
      <px-cadastro-produto-campos-adicionais dense
                                             ref="camposAdicionais"
                                             show-cadastros-adicionais
                                             cadastros-adicionais-type="cotacao-item"
                                             label-previsao-entrega="Prazo de entrega"
                                             :campos-readonly="camposReadonly"
                                             :is-from-requisicao="isFromRequisicao"
                                             :cadastros-adicionais="produto.cadastrosAdicionais"
                                             :campos-obrigatorios="camposObrigatorios"
                                             :produto="produto"
                                             @replicar-campo="onReplicarCampo"/>
      <v-tooltip top v-if="!hideAcoes && !isFromRequisicao">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="duplicar(produto)">
            <v-icon small>mdi-content-copy</v-icon>
          </v-btn>
        </template>
        <span>Copiar produto</span>
      </v-tooltip>
      <v-tooltip top v-if="canRemove && !hideAcoes">
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="removeProduto">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </template>
        <span>Remover produto</span>
      </v-tooltip>
    </td>
  </fragment>
</template>
<script>
import { required } from 'vuelidate/lib/validators';
import { Fragment } from 'vue-frag';
import { getPrecision } from '@/utils/produto-utils';
import validationMixin from '@/mixins/validation-mixin';
import validationFileMixin from '@/mixins/validation-file-mixin';
import { validateCamposErp, computeFrom, hasCamposAdicionaisErp } from './campos-erp/validate-campos-erp';

export default {
  mixins: [validationMixin, validationFileMixin],
  props: {
    produto: {
      type: Object,
      required: true,
    },
    isRascunho: {
      type: Boolean,
      default: false,
    },
    hideAcoes: {
      type: Boolean,
      default: false,
    },
    isFromRequisicao: {
      type: Boolean,
      default: false,
    },
    camposReadonly: {
      type: Object,
      default: null,
    },
    camposObrigatorios: {
      type: Object,
      default: null,
    },
    erpCamposIntegracao: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      initUnidadeMedida: true,
    };
  },
  computed: {
    canRemove() {
      return this.isRascunho || !this.produto.id;
    },
    getPrecision() {
      return item => getPrecision(item);
    },
  },
  methods: {
    validate() {
      const isInvalid = [
        this.validateForm(),
        this.validateFile(),
        this.$refs.camposAdicionais.validate(),
        !this.validateCadastrosAdicionais(),
      ].some(v => v);
      return !isInvalid;
    },
    validateCadastrosAdicionais() {
      const [isValid, message] = validateCamposErp(
        this.erpCamposIntegracao,
        this.produto.cadastrosAdicionais,
      );
      if (!isValid) {
        this.$toast(message, { color: 'error' });
      }
      return isValid;
    },
    getUnidadeMedida(produto) {
      if (this.initUnidadeMedida) {
        this.initUnidadeMedida = false;
        return produto.unidadeMedida?.id ? produto.unidadeMedida : this.getUnidadeMedida(produto);
      }
      return produto.produto ? { ...produto.produto.unidadeMedida } : null;
    },
    getEspecificacao(produto) {
      if (produto.especificacaoProduto) {
        return produto.especificacaoProduto;
      }
      return produto.produto ? produto.produto.especificacao : null;
    },
    getArquivos(produto) {
      if (produto.arquivos && produto.arquivos.length) {
        return produto.arquivos;
      }
      return produto.produto ? produto.produto.arquivos : [];
    },
    getObservacaoExterna(produto) {
      if (produto.observacaoExterna) {
        return produto.observacaoExterna;
      }
      return produto.produto ? produto.produto.observacaoExterna : '';
    },
    getObservacaoInterna(produto) {
      if (produto.observacaoInterna) {
        return produto.observacaoInterna;
      }
      return produto.produto ? produto.produto.observacaoInterna : null;
    },
    getFinalidadeCompra(produto) {
      const finalidadeCompra = produto.produto ? produto.produto.finalidadeCompra : null;
      return finalidadeCompra ?? produto.finalidadeCompra;
    },
    getCadastrosAdicionais(produto) {
      if (hasCamposAdicionaisErp(this.erpCamposIntegracao, produto.cadastrosAdicionais)) {
        return produto.cadastrosAdicionais;
      }
      return computeFrom(
        this.erpCamposIntegracao,
        produto.produto.cadastrosAdicionais,
      );
    },
    onReplicarCampo(campo) {
      this.$emit('replicar-campo', campo);
    },
    onChange(produto) {
      this.produto.especificacaoProduto = this.getEspecificacao(produto);
      this.produto.unidadeMedida = this.getUnidadeMedida(produto);
      this.produto.arquivos = this.getArquivos(produto);
      this.produto.observacaoExterna = this.getObservacaoExterna(produto);
      this.produto.observacaoInterna = this.getObservacaoInterna(produto);
      this.produto.finalidadeCompra = this.getFinalidadeCompra(produto);
      this.produto.cadastrosAdicionais = this.getCadastrosAdicionais(produto);
    },
    duplicar(item) {
      this.$emit('duplicar-produto', item);
    },
    removeProduto() {
      this.$emit('remove-produto');
    },
  },
  validations: {
    produto: {
      produto: {
        required,
      },
      quantidade: {
        required,
      },
      unidadeMedida: {
        id: {
          required,
        },
      },
    },
  },
  components: {
    Fragment,
  },
};
</script>
